import { z } from "zod";

export const InsuranceNetworkStatus = {
  InNetwork: "in_network",
  ManualReview: "manual_review",
  NotApplicable: "not_applicable",
  OutOfNetwork: "out_of_network",
} as const;
export type InsuranceNetworkStatus = (typeof InsuranceNetworkStatus)[keyof typeof InsuranceNetworkStatus];

export const insuranceNetworkStatusSchema = z.enum(InsuranceNetworkStatus);

export enum InsuranceRequirementStatus {
  ManualReview = "manual_review",
  NotApplicable = "not_applicable",
  NotRequired = "not_required",
  Required = "required",
}

export const insuranceRequirementStatusSchema = z.enum(InsuranceRequirementStatus);

export const insuranceNetworkVerificationScopeSchema = z.object({
  insuranceId: z.string().min(1),
  packageNumber: z.string().min(1),
  providerNpi: z.string().regex(/^\d{10}$/),
  departmentId: z.string().min(1),
});

export const insuranceNetworkVerificationSchema = insuranceNetworkVerificationScopeSchema.extend({
  // Older task-only confirmations predate patient-scoped persistence.
  patientId: z.string().uuid().optional(),
  variantKey: z.string().optional(),
  verifiedBy: z.string().min(1),
  verifiedAt: z.string().datetime(),
});

export const patientInsuranceNetworkVerificationSchema = insuranceNetworkVerificationSchema.extend({
  patientId: z.string().uuid(),
});
export type PatientInsuranceNetworkVerification = z.infer<typeof patientInsuranceNetworkVerificationSchema>;

// Read older rows without a variant, but require one on every new patient confirmation.
export const patientInsuranceNetworkVerificationInputSchema = patientInsuranceNetworkVerificationSchema.required({
  variantKey: true,
});
export type PatientInsuranceNetworkVerificationInput = z.infer<typeof patientInsuranceNetworkVerificationInputSchema>;

export const confirmInsuranceNetworkRequestSchema = insuranceNetworkVerificationScopeSchema.extend({
  patientId: z.string().uuid(),
  expectedVariantKey: z.string(),
});
export type ConfirmInsuranceNetworkRequest = z.infer<typeof confirmInsuranceNetworkRequestSchema>;

export const confirmInsuranceNetworkResponseSchema = z.object({ success: z.literal(true) });
export type InsuranceNetworkVerificationScope = z.infer<typeof insuranceNetworkVerificationScopeSchema>;
export type InsuranceNetworkVerification = z.infer<typeof insuranceNetworkVerificationSchema>;

/** Patient evidence must identify the exact recognized variant; older evidence stays task-local. */
export function matchesInsuranceNetworkVerificationScope(
  verification: InsuranceNetworkVerification,
  scope: {
    patientId?: string | null;
    insuranceId?: string | null;
    packageNumber: string;
    providerNpi?: string | null;
    departmentId?: string | null;
    variantKey: string | null;
  },
  allowLegacyTaskVerification = false
): boolean {
  const patientMatches = verification.patientId
    ? verification.patientId === scope.patientId
    : allowLegacyTaskVerification;
  const variantMatches =
    scope.variantKey !== null &&
    (verification.variantKey === scope.variantKey ||
      (allowLegacyTaskVerification && verification.variantKey === undefined && scope.variantKey === ""));

  return (
    patientMatches &&
    variantMatches &&
    verification.insuranceId === scope.insuranceId &&
    verification.packageNumber === scope.packageNumber &&
    verification.providerNpi === scope.providerNpi &&
    verification.departmentId === scope.departmentId
  );
}

/** Provider-specific insurance facts for scheduling eligibility to evaluate. */
export const providerInsuranceRequirementsSchema = z.object({
  packageNumber: z.string(),
  planLabels: z.array(z.string()),
  clinicNetwork: insuranceNetworkStatusSchema,
  providerNetwork: insuranceNetworkStatusSchema,
  authorizationRequirement: insuranceRequirementStatusSchema,
  referralRequirement: insuranceRequirementStatusSchema,
  referralMustBeFromPcp: z.boolean().optional().default(false),
  additionalSchedulingInformationRequired: z.boolean(),
  reviewReasons: z.array(z.string()),
});

export type ProviderInsuranceRequirements = z.infer<typeof providerInsuranceRequirementsSchema>;
